import Chart from 'chart.js/auto'; // Auto register chart.js components
import ChartDataLabels from 'chartjs-plugin-datalabels';  // Import the datalabels plugin

function generateDemographicHTML(sortedData: [string, { count: number, color: string, title: string }][]): string {
    // Generate HTML for demographic labels with color, title, and count
    return sortedData.map(([key, data]) => {
        return `
            <div class="demographic-item">
                <div class="color-box" style="background-color: ${data.color};"></div>
                <div class="title">${data.title}</div>
                <div class="count">${data.count}%</div>
            </div>
        `;
    }).join('');
}

function initDemographicChart(): void {
    // Check if the demographic data block is present on the page
    const demographicBlock = document.querySelector('.demographic-block') as HTMLElement;


    // Setup the chart.js datalabels plugin options
    const chartjs_datalabel_plugin = {
        borderWidth: 1,
        color: 'white',
        padding: 10,
        textAlign: 'center' as const,
        font: {
            weight: 'bold' as const,
            size: 16
        },
        formatter: (value: number, context: any) => {
            if (value > 15) {
                let label = context.chart.data.labels[context.dataIndex].length <= 16
                    ? context.chart.data.labels[context.dataIndex]
                    : '';
                label = label.replaceAll(' ', '\n');
                return `${value}%\n\r${label}`;
            } else {
                return null;
            }
        },
        anchor: 'end' as const,
        align: 'end' as const,
        offset: -120,
    };

    const chartjs_options = {
        plugins: {
            legend: {
                display: false
            },
            datalabels: chartjs_datalabel_plugin
        }
    };

    const studentPercent = (count: number, total: number) => {
        return total !== 0 ? Number(((count / total) * 100).toFixed(2)) : 0;
    };

    if (demographicBlock) {
        // Get total count and prepare data with colors
        const totalCount = parseInt(demographicBlock.getAttribute('data-count') || '0', 10);
        const dataWithColors: { [key: string]: { count: number | null, color: string, title: string } } = {
            Hispanic: {
                count: studentPercent(parseInt(demographicBlock.getAttribute('data-hispanic') || '', 10), totalCount),
                color: '#cf4016',
                title: 'Hispanic',
            },
            AfricanAmerican: {
                count: studentPercent(parseInt(demographicBlock.getAttribute('data-africanAmerican') || '', 10), totalCount),
                color: '#4d0e5a',
                title: 'African American',
            },
            White: {
                count: studentPercent(parseInt(demographicBlock.getAttribute('data-white') || '', 10), totalCount),
                color: '#50c4d9',
                title: 'White'
            },
            Asian: {
                count: studentPercent(parseInt(demographicBlock.getAttribute('data-asian') || '', 10), totalCount),
                color: '#061a53',
                title: 'Asian'
            },
            MultiEthnic: {
                count: studentPercent(parseInt(demographicBlock.getAttribute('data-multiEthnic') || '', 10), totalCount),
                color: '#047a6e',
                title: 'Multi-Racial'
            },
            NativeAmerican: {
                count: studentPercent(parseInt(demographicBlock.getAttribute('data-nativeAmerican') || '', 10), totalCount),
                color: '#7f2792',
                title: 'Native American/Alaskan'
            },
            EthnicityOther: {
                count: studentPercent(parseInt(demographicBlock.getAttribute('data-ethnicityOther') || '', 10), totalCount),
                color: '#fb7847',
                title: 'Not Available'
            },
            Hawaiian: {
                count: studentPercent(parseInt(demographicBlock.getAttribute('data-hawaiian') || '', 10), totalCount),
                color: '#e2e2e2',
                title: 'Hawaiian/Pacific Islander'
            },
            AsianPacific: {
                count: studentPercent(parseInt(demographicBlock.getAttribute('data-asianPacific') || '', 10), totalCount),
                color: '#ffffff',
                title: 'Asian/Pacific Islander (retired)'
            },
        };

        // Filter out only empty values (not zeros)
        const filteredData = Object.entries(dataWithColors)
            .filter(([_, data]) => data.count !== null)
            .sort(([, a], [, b]) => (b.count ?? 0) - (a.count ?? 0))
            .reduce((acc, [key, data]) => ({ ...acc, [key]: data }), {});

        // Prepare labels, values, and colors for the chart
        const keys = Object.keys(filteredData)
        const labels = keys.map(label => filteredData[label].title);
        const values = keys.map(label => filteredData[label].count);
        const colors = keys.map(label => filteredData[label].color);


        const infoBlock = document.querySelector('.demographic-info-block');
        if (infoBlock) {
            const sortedData = Object.entries(dataWithColors)
                .filter(([_, data]) => data.count !== null)
                .sort(([, a], [, b]) => (b.count ?? 0) - (a.count ?? 0))
            infoBlock.innerHTML = generateDemographicHTML(sortedData);
        }
        // Get the canvas context where the chart will be displayed
        const ctx = demographicBlock.querySelector('.demographicChart') as HTMLCanvasElement;

        // Create a new pie chart using Chart.js
        if (ctx) {
            new Chart(ctx, {
                type: 'pie',
                data: {
                    labels: labels,
                    datasets: [{
                        data: values,
                        backgroundColor: colors, // Colors for each segment
                    }],
                },
                options: chartjs_options,  // Apply options here
            });
        } else {
            console.log('Canvas element for the chart not found.');
        }
    } else {
        console.log('Demographic block not found on the page.');
    }
}

// Initialize the chart
initDemographicChart();